import {Injectable} from '@angular/core';
import {Platform} from 'ionic-angular';
import {MediaProvider} from '../media/media';
import * as RecordRTC from 'recordrtc';
import * as moment from 'moment';
import {Observable, Subject} from 'rxjs';
import {FileProvider} from '../file/file';
import {ProofServerProvider} from "../proof-server/proof-server";

interface RecordedAudioOutput {
  blob: Blob;
  title: string;
}

@Injectable()
export class ProofAudioProvider {
  private proofId: number = null;
  private stream: MediaStream;
  private recorder: any;
  private interval: any;
  private startTime: moment.Moment;
  private _recorded = new Subject<RecordedAudioOutput>();
  private _recordingTime = new Subject<string>();
  private _recordingFailed = new Subject<string>();
  private _streamActive = new Subject<boolean>();

  constructor(
    private platform: Platform,
    private mediaProvider: MediaProvider,
    private fileProvider: FileProvider,
    private proofServer: ProofServerProvider
  ) {
    console.log('ProofAudioProvider initialized');
  }

  initiate(proofId: number, questionId: number, seconds: number, secondsRemaining: number) {
    this.proofId = proofId;
    this.platform.ready().then(() => {
      this.takeAudio(questionId, seconds, secondsRemaining);
      console.log('ProofAudioProvider - Initiated');
    });
  }

  takeAudio(questionId: number, seconds: number, secondsRemaining: number) {
    if (this.proofId) {
      this.mediaProvider.takeAudio(questionId, seconds, secondsRemaining);
    }
  }

  getRecordedBlob(): Observable<RecordedAudioOutput> {
    return this._recorded.asObservable();
  }

  getRecordedTime(): Observable<string> {
    return this._recordingTime.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this._recordingFailed.asObservable();
  }

  /**
   * Observable for stream active state changes
   */
  getStreamActive(): Observable<boolean> {
    return this._streamActive.asObservable();
  }

  startRecording() {
    this._recordingTime.next('00:00');

    // Check if mediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.log('ProofAudioProvider - MediaDevices API not available');
      this._recordingFailed.next();
      return;
    }

    // First check if a microphone device exists
    this.checkMicrophoneExists().then((hasMicrophone) => {
      if (!hasMicrophone) {
        console.log('ProofAudioProvider - No microphone device found');
        this._recordingFailed.next();
        return;
      }

      navigator.mediaDevices.getUserMedia({audio: true}).then(stream => {
        this.stream = stream;
        this._streamActive.next(true);
        this.monitorAudioTrack(stream);
        this.record();
      }).catch((err) => {
        console.log('ProofAudioProvider - getUserMedia failed:', err.name, err.message);
        this._streamActive.next(false);
        this._recordingFailed.next();
      });
    }).catch(() => {
      // If we can't check, try anyway
      navigator.mediaDevices.getUserMedia({audio: true}).then(stream => {
        this.stream = stream;
        this._streamActive.next(true);
        this.monitorAudioTrack(stream);
        this.record();
      }).catch(() => {
        this._streamActive.next(false);
        this._recordingFailed.next();
      });
    });
  }

  /**
   * Check if a microphone device exists
   */
  private checkMicrophoneExists(): Promise<boolean> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return Promise.resolve(true); // Assume exists if we can't check
    }

    return navigator.mediaDevices.enumerateDevices()
      .then((devices) => devices.some(device => device.kind === 'audioinput'))
      .catch(() => true); // Assume exists on error
  }

  abortRecording() {
    this.stopMedia();
  }

  private record() {
    this.recorder = new RecordRTC.StereoAudioRecorder(this.stream, {
      type: 'audio',
      mimeType: 'audio/webm'
    });

    this.recorder.record();
    this.startTime = moment();

    this.interval = setInterval(() => {
      const diffTime = moment.duration(moment().diff(this.startTime));
      const formattedTime = `${this.formatTime(diffTime.minutes())}:${this.formatTime(diffTime.seconds())}`;
      this._recordingTime.next(formattedTime);
    }, 1000);
  }

  private formatTime(value: number): string {
    return value < 10 ? `0${value || 0}` : `${value}`;
  }

  stopRecording(
    questionId: number, kind: string, note: string, extra: string,
    captured: any, seconds: number, secondsRemaining: number
  ) {
    if (this.recorder) {
      this.recorder.stop((blob: Blob) => {
        if (this.startTime) {
          const mp3Name = `${encodeURIComponent(this.fileProvider.generateName())}.mp3`;
          this._recorded.next({blob, title: mp3Name});
          this.stopMedia();
          this.proofServer.saveAudio(blob, mp3Name, questionId, kind, note, extra, captured, seconds, secondsRemaining);
        }
      }, () => {
        this.stopMedia();
        this._recordingFailed.next();
      });
    }
  }

  private stopMedia() {
    if (this.recorder) {
      this.recorder = null;
      clearInterval(this.interval);
      this.startTime = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
      this._streamActive.next(false);
    }
  }

  /**
   * Monitor audio track state changes
   */
  private monitorAudioTrack(stream: MediaStream): void {
    const audioTracks = stream.getAudioTracks();
    if (audioTracks && audioTracks.length > 0) {
      const audioTrack = audioTracks[0];

      audioTrack.onended = () => {
        console.log('ProofAudioProvider - Audio track ended');
        this._streamActive.next(false);
      };

      audioTrack.onmute = () => {
        console.log('ProofAudioProvider - Audio track muted');
        this._streamActive.next(false);
      };

      audioTrack.onunmute = () => {
        console.log('ProofAudioProvider - Audio track unmuted');
        this._streamActive.next(true);
      };
    }
  }

  destroy() {
    console.log('ProofAudioProvider - Destroyed');
    this.proofId = null;
  }

}




// WEBPACK FOOTER //
// ./src/providers/proof-audio/proof-audio.ts